/**
 * Ajout AGORA (absent du bundle de référence) – squelettes de chargement (décision du 26/10/2026) : pendant un
 * chargement, l'écran montre la forme de ce qui arrive (barre de titre, champs, lignes d'une liste) au lieu d'un texte
 * « Chargement… » seul. Le contenu remplace le squelette à la même place : pas de saut de mise en page.
 *
 *  - `Skeleton` : forme décorative (ligne de texte, titre, bloc, cercle ; tailles sur les jetons), cachée aux lecteurs
 *    d'écran (`aria-hidden`) ;
 *  - `SkeletonGroup` : zone en chargement composée librement de `Skeleton` (statut lu + zone `aria-busy`) ;
 *  - `RecordSkeleton` : squelette d'une fiche (barre de titre, cartes de champs) ;
 *  - `ListSkeleton` : squelette d'une liste d'éléments (icône et deux lignes : pièces jointes, fil de messages) ;
 *  - tableau : prop `loading` du `DataTable` (donnees.ts), lignes squelettes qui reprennent les colonnes.
 *
 * Accessibilité : les formes sont décoratives ; la zone qui charge porte `aria-busy="true"` et le libellé traduit
 * (`app.chargement`, fourni par l'appelant) est lu une fois par les lecteurs d'écran (`role="status"`, texte
 * visuellement masqué). Le statut est placé à côté de la zone occupée, jamais dedans : un lecteur d'écran peut taire
 * ce qui change dans une zone `aria-busy` jusqu'à la fin du chargement, et le statut disparaît avec le squelette.
 * Styles et mouvement (reflet sur les jetons, coupé si le mouvement est réduit, aucun flou) : ./squelette.css.
 * Règles et adoption : web/README.md › « Chargements (squelettes) ».
 */
import React from 'react';
import { cx, h } from './base';
import './squelette.css';

/* ---------- Skeleton ---------- */
export type SkeletonVariant = 'text' | 'title' | 'block' | 'circle';
export type SkeletonSize = 'sm' | 'md' | 'lg';
export interface SkeletonProps {
  /**
   * Forme : `text` ligne de texte courant (défaut), `title` titre de page, `block` bloc (graphique, image, carte),
   * `circle` cercle (avatar, icône).
   */
  variant?: SkeletonVariant;
  /** Largeur en pourcentage de la place disponible (texte, titre, bloc) ; défaut 100 %, 40 % pour un titre. */
  width?: number;
  /**
   * Hauteur d'un bloc ou diamètre d'un cercle, sur les jetons (squelette.css) – bloc : `sm` 48 px, `md` 96 px,
   * `lg` 192 px (`--space-7` × 1, 2, 4) ; cercle : `sm` `--control-sm`, `md` `--control-md`, `lg` `--control-touch`.
   */
  size?: SkeletonSize;
  className?: string;
}

const CLASSE_FORME: Record<SkeletonVariant, string> = { text: 'agx-squelette-texte', title: 'agx-squelette-titre', block: 'agx-squelette-bloc', circle: 'agx-squelette-cercle' };

export function Skeleton(p: SkeletonProps) {
  const forme = p.variant || 'text';
  const taille = forme === 'block' || forme === 'circle' ? `agx-squelette-${p.size || 'md'}` : null;
  return h('span', {
    className: cx('agx-squelette', CLASSE_FORME[forme], taille, p.className), 'aria-hidden': true,
    style: p.width !== undefined && forme !== 'circle' ? { inlineSize: `${p.width}%` } : undefined,
  });
}

/* ---------- Statut lu pendant un chargement (commun aux squelettes et au DataTable) ---------- */
/** Statut lu une fois par les lecteurs d'écran (texte visuellement masqué) ; rien sans libellé. */
export function statutChargement(label: string | undefined): React.ReactElement | null {
  return label ? h('span', { className: 'agx-squelette-statut', role: 'status' }, label) : null;
}

/* ---------- SkeletonGroup ---------- */
export interface SkeletonGroupProps {
  /** Statut lu une fois par les lecteurs d'écran, traduit (`app.chargement`). */
  label: string;
  /** Formes (`Skeleton`) disposées comme le contenu attendu. */
  children?: React.ReactNode;
  className?: string;
}

/** Zone en chargement : statut lu, puis les formes dans une zone `aria-busy="true"` (sans contenu lisible). */
export function SkeletonGroup(p: SkeletonGroupProps) {
  return h('div', { className: cx('agx-squelette-groupe', p.className) },
    statutChargement(p.label),
    h('div', { className: 'agx-squelette-formes', 'aria-busy': true }, p.children));
}

/* ---------- RecordSkeleton ---------- */
export interface RecordSkeletonProps {
  /** Statut lu une fois par les lecteurs d'écran, traduit (`app.chargement`). */
  label: string;
  /** Barre de titre de la page (titre et sous-titre) ; `false` quand l'écran affiche déjà son en-tête. Défaut : vrai. */
  header?: boolean;
  /** Nombre de cartes (défaut 2), dans la grille des fiches (`agx-grille`). */
  cards?: number;
  /** Nombre de champs (libellé + valeur) par carte (défaut 5). */
  fields?: number;
}

/** Largeurs (%) des valeurs, pour un rendu moins mécanique ; toujours les mêmes d'un affichage à l'autre. */
const LARGEURS_VALEURS = [72, 48, 86, 60, 78, 54, 66];

/** Squelette d'une fiche : barre de titre (titre, sous-titre) et cartes de champs, comme FicheTiers.tsx. */
export function RecordSkeleton(p: RecordSkeletonProps) {
  const cartes = Math.max(0, p.cards ?? 2);
  const champs = Math.max(0, p.fields ?? 5);
  return h(SkeletonGroup, { label: p.label, className: 'agx-squelette-fiche' },
    p.header === false ? null : h('div', { className: 'ag-pagehead' },
      h('div', { className: 'agx-squelette-titres' },
        h(Skeleton, { variant: 'title', width: 40 }),
        h(Skeleton, { width: 24 }))),
    cartes ? h('div', { className: 'agx-grille' }, Array.from({ length: cartes }, (_, c) =>
      h('div', { key: c, className: 'ag-glass-card agx-carte' },
        h(Skeleton, { width: 36 }),
        h('div', { className: 'agx-squelette-champs' }, Array.from({ length: champs }, (_, i) => i).flatMap((i) => [
          h(Skeleton, { key: `l${i}`, width: 70 }),
          h(Skeleton, { key: `v${i}`, width: LARGEURS_VALEURS[(c * 3 + i) % LARGEURS_VALEURS.length] }),
        ]))))) : null);
}

/* ---------- ListSkeleton ---------- */
export interface ListSkeletonProps {
  /** Statut lu une fois par les lecteurs d'écran, traduit (`app.chargement`). */
  label: string;
  /** Nombre d'éléments (défaut 3). */
  rows?: number;
}

/** Squelette d'une liste d'éléments : icône, nom et ligne secondaire (pièces jointes, messages, appels). */
export function ListSkeleton(p: ListSkeletonProps) {
  return h(SkeletonGroup, { label: p.label, className: 'agx-squelette-liste' },
    Array.from({ length: Math.max(0, p.rows ?? 3) }, (_, i) =>
      h('div', { key: i, className: 'agx-squelette-element' },
        h(Skeleton, { variant: 'circle', size: 'md' }),
        h('div', { className: 'agx-squelette-lignes' },
          h(Skeleton, { width: LARGEURS_VALEURS[i % LARGEURS_VALEURS.length] }),
          h(Skeleton, { className: 'agx-squelette-petit', width: 32 })))));
}
